import { Check, Copy, ExternalLink } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, LoadingState, useToast } from '@buildbee/ui';
import { trpc } from '../../../lib/trpc';
import { useWorkspaceSlug } from '../../../lib/workspace';
import type { CompanyBrand } from '../../../shell/brand';

/**
 * Company sign-in (/c/<slug>/...): the team's own branded sign-in page on the shared host.
 *
 * Branding comes from the public `portal.auth.branding` lookup (rate limited per IP, one D1 read
 * plus a signed logo URL) that the client portal already uses. It returns only branding fields.
 * An unknown slug quietly falls back to the standard BuildBee sign-in.
 */
export const BRANDING_STALE_MS = 10 * 60_000;

export function useCompanyBranding(slug: string) {
  return trpc.portal.auth.branding.useQuery(
    { workspaceSlug: slug },
    {
      staleTime: BRANDING_STALE_MS,
      retry: false,
      select: (b): CompanyBrand | null => (b ? { name: b.name, slug: b.slug, logoUrl: b.logoUrl, primaryColor: b.primaryColor, companyCode: b.companyCode } : null),
    },
  );
}

/** Loads the company's branding, then renders the page with it (or without, if unknown). */
export function WithCompany({ slug, children }: { slug: string; children: (company: CompanyBrand | undefined) => ReactNode }) {
  const branding = useCompanyBranding(slug);
  if (branding.isLoading) return <LoadingState className="min-h-dvh" label="Loading sign-in" />;
  // Errors (including rate limits) fall back to the standard sign-in rather than blocking it.
  return <>{children(branding.data ?? undefined)}</>;
}

/** The shareable company sign-in address for a workspace slug. */
export function companySignInUrl(origin: string, slug: string): string {
  return `${origin.replace(/\/$/, '')}/c/${encodeURIComponent(slug)}`;
}

/**
 * "Your branded login link" box for Settings > Branding: the team sign-in address with a copy
 * button, plus the client portal address. Uses the active workspace.
 */
export function BrandedLoginLinkCard({ origin = typeof window !== 'undefined' ? window.location.origin : '' }: { origin?: string }) {
  const slug = useWorkspaceSlug();
  if (!slug) return null;
  const team = companySignInUrl(origin, slug);
  const portal = `${origin.replace(/\/$/, '')}/portal/${encodeURIComponent(slug)}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your branded login link</CardTitle>
        <CardDescription>Share it with your team. The sign-in page shows your logo, name and colour.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <LinkRow label="Team sign-in" value={team} />
        <LinkRow label="Client portal" value={portal} />
        <p className="text-xs text-muted-foreground">If you change the workspace URL, these links change too and old links stop working.</p>
      </CardContent>
    </Card>
  );
}

function LinkRow({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const id = `link-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(`${label} link copied`);
    } catch {
      toast.error('Could not copy', 'Select the link and copy it yourself.');
    }
  };
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <div className="flex gap-2">
        <Input id={id} readOnly value={value} className="figures min-w-0 flex-1" onFocus={(e) => e.currentTarget.select()} />
        <Button type="button" variant="outline" size="icon" aria-label={`Copy ${label.toLowerCase()} link`} onClick={() => void copy()}>
          {copied ? <Check /> : <Copy />}
        </Button>
        <Button type="button" variant="ghost" size="icon" asChild>
          <a href={value} target="_blank" rel="noreferrer" aria-label={`Open ${label.toLowerCase()} page`}>
            <ExternalLink />
          </a>
        </Button>
      </div>
    </div>
  );
}
